/* Enhances Impulse's existing media slider and PhotoSwipe; no new dependency. */
(() => {
  'use strict';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const galleries = new WeakMap();
  const visibleSlides = root => Array.from(root.querySelectorAll('.flickity-slider .product-main-slide, .product-slideshow > .product-main-slide')).filter(el => !el.classList.contains('hide'));
  const keepInView = (strip, item) => {
    if (!item) return;
    const box = item.getBoundingClientRect();
    const bounds = strip.getBoundingClientRect();
    const vertical = getComputedStyle(strip).flexDirection === 'column';
    if (vertical && (box.top < bounds.top || box.bottom > bounds.bottom)) {
      strip.scrollBy({ top: box.top - bounds.top - (bounds.height - box.height) / 2, behavior: reducedMotion() ? 'instant' : 'smooth' });
    } else if (!vertical && (box.left < bounds.left || box.right > bounds.right)) {
      strip.scrollBy({ left: box.left - bounds.left - (bounds.width - box.width) / 2, behavior: reducedMotion() ? 'instant' : 'smooth' });
    }
  };

  if (window.theme?.Slideshow) {
    const originalChildNav = theme.Slideshow.prototype.childNavGoTo;
    theme.Slideshow.prototype.childNavGoTo = function(index) {
      if (!this.el.closest('[data-eo-gallery]')) return originalChildNav.call(this, index);
      const active = this.childNavLinks[index];
      if (!active) return;
      this.childNavLinks.forEach(link => link.classList.toggle('is-active', link === active));
      if (this.args.childNavScroller) keepInView(this.args.childNavScroller, active);
    };
  }

  function setupGallery(root) {
    if (galleries.has(root)) return;
    const slider = root.querySelector('[data-product-photos]');
    const count = root.querySelector('[data-eo-count]');
    const type = root.querySelector('[data-eo-type]');
    const enlarge = root.querySelector('[data-eo-enlarge]');
    const prev = root.querySelector('[data-eo-prev]');
    const next = root.querySelector('[data-eo-next]');
    const scroller = root.querySelector('.product__thumbs--scroller');
    const railPrev = root.querySelector('[data-eo-rail="previous"]');
    const railNext = root.querySelector('[data-eo-rail="next"]');
    if (!slider || !count || !scroller) return;
    const syncRail = () => {
      if (!railPrev || !railNext) return;
      const vertical = getComputedStyle(scroller).flexDirection === 'column';
      const position = vertical ? scroller.scrollTop : scroller.scrollLeft;
      const extent = vertical ? scroller.scrollHeight - scroller.clientHeight : scroller.scrollWidth - scroller.clientWidth;
      railPrev.disabled = position <= 2;
      railNext.disabled = position >= extent - 2;
    };
    const pageRail = direction => {
      const amount = Math.max(82, scroller.clientHeight - 90) * direction;
      scroller.scrollBy({ top: amount, behavior: reducedMotion() ? 'instant' : 'smooth' });
    };
    railPrev?.addEventListener('click', () => pageRail(-1));
    railNext?.addEventListener('click', () => pageRail(1));
    scroller.addEventListener('scroll', syncRail, { passive: true });
    const railResize = new ResizeObserver(() => {
      keepInView(scroller, root.querySelector('[data-product-thumb][aria-current="true"]'));
      syncRail();
    });
    railResize.observe(scroller);
    let lastSignature = '';
    let frame;
    const selected = () => slider.querySelector('.product-main-slide.is-selected') || visibleSlides(root)[0];
    const select = (index, focusThumbnail = false) => {
      const slides = visibleSlides(root);
      const slide = slides[(index + slides.length) % slides.length];
      if (!slide) return;
      const thumb = root.querySelector(`.product__thumb-item[data-index="${slide.dataset.index}"] [data-product-thumb]`);
      thumb?.click();
      if (focusThumbnail) thumb?.focus({ preventScroll: true });
    };
    const move = (delta, focusThumbnail = false) => select(visibleSlides(root).indexOf(selected()) + delta, focusThumbnail);
    const sync = () => {
      const slides = visibleSlides(root);
      const active = selected();
      if (!active) return;
      const signature = `${active.dataset.index}:${slides.map(el => el.dataset.index).join(',')}`;
      if (signature === lastSignature) return;
      lastSignature = signature;
      const index = slides.indexOf(active);
      const mediaType = active.dataset.mediaType;
      const label = mediaType === 'model' ? type.dataset.model : /video/.test(mediaType) ? type.dataset.video : type.dataset.photo;
      count.textContent = `${index + 1} / ${slides.length}`;
      count.setAttribute('aria-label', `${label} ${index + 1} / ${slides.length}`);
      type.textContent = label;
      prev.hidden = next.hidden = slides.length < 2;
      if (enlarge) enlarge.hidden = mediaType !== 'image';
      root.querySelectorAll('[data-product-thumb]').forEach(thumb => {
        const current = thumb.closest('.product__thumb-item').dataset.index === active.dataset.index;
        thumb.setAttribute('aria-current', current ? 'true' : 'false');
      });
      keepInView(scroller, root.querySelector('[data-product-thumb][aria-current="true"]'));
      syncRail();
      slides.forEach(slide => {
        slide.querySelectorAll('button, a, video, model-viewer, iframe').forEach(control => {
          if (slide !== active) {
            if (!control.hasAttribute('data-eo-tabindex')) control.dataset.eoTabindex = control.getAttribute('tabindex') ?? '';
            control.setAttribute('tabindex', '-1');
          } else if (control.hasAttribute('data-eo-tabindex')) {
            const previous = control.dataset.eoTabindex;
            previous === '' ? control.removeAttribute('tabindex') : control.setAttribute('tabindex', previous);
            delete control.dataset.eoTabindex;
          }
        });
      });
    };
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    });
    observer.observe(slider, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    prev.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    enlarge?.addEventListener('click', () => selected()?.querySelector('.js-photoswipe__zoom')?.click());
    root.addEventListener('keydown', event => {
      if (event.target.closest('video, model-viewer, iframe, input, select, textarea')) return;
      if (event.code === 'Space' && event.target.matches('[data-product-thumb]')) {
        event.preventDefault(); event.target.click(); return;
      }
      const vertical = getComputedStyle(scroller).flexDirection === 'column';
      const keys = vertical && event.target.closest('[data-product-thumbs]') ? { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -1, ArrowDown: 1 } : { ArrowLeft: -1, ArrowRight: 1 };
      const focusThumbnail = event.target.matches('[data-product-thumb]');
      if (event.key in keys) { event.preventDefault(); move(keys[event.key], focusThumbnail); }
      if (event.key === 'Home') { event.preventDefault(); select(0, focusThumbnail); }
      if (event.key === 'End') { event.preventDefault(); select(visibleSlides(root).length - 1, focusThumbnail); }
    });
    root.dataset.eoGalleryReady = '';
    sync();
    galleries.set(root, () => { observer.disconnect(); railResize.disconnect(); cancelAnimationFrame(frame); });
  }

  document.addEventListener('eo:gallery-open', event => {
    const root = event.target;
    if (!root.matches('[data-eo-gallery]')) return;
    const { viewer, items } = event.detail;
    const dialog = document.querySelector('.pswp');
    const strip = dialog.querySelector('[data-eo-viewer-thumbs]');
    const zoom = dialog.querySelector('[data-eo-viewer-zoom]');
    const count = dialog.querySelector('[data-eo-viewer-count]');
    const caption = dialog.querySelector('[data-eo-viewer-caption]');
    const close = dialog.querySelector('.pswp__button--close');
    const returnFocus = root.contains(document.activeElement) ? document.activeElement : root.querySelector('[data-eo-enlarge]');
    const controller = new AbortController();
    const listen = (el, name, callback, options = {}) => el.addEventListener(name, callback, { ...options, signal: controller.signal });
    dialog.classList.add('eo-viewer');
    dialog.setAttribute('aria-modal', 'true');
    dialog.querySelector('[data-eo-viewer-title]').textContent = root.dataset.galleryTitle;
    dialog.querySelectorAll('.pswp__button').forEach(button => button.setAttribute('aria-label', button.title));
    const unavailable = items.length < 2;
    dialog.querySelectorAll('.pswp__button--arrow--left, .pswp__button--arrow--right').forEach(button => { button.hidden = unavailable; });
    strip.replaceChildren();
    const photoLabel = root.querySelector('[data-eo-type]').dataset.photo;
    items.forEach((item, index) => {
      item.src = new URL(item.src, document.baseURI).href;
      item.msrc = new URL(item.msrc, document.baseURI).href;
      item.alt = item.el.alt || `${root.dataset.galleryTitle} — ${photoLabel} ${index + 1}`;
      const originalIndex = item.el.closest('.product-main-slide').dataset.index;
      const source = root.querySelector(`.product__thumb-item[data-index="${originalIndex}"] [data-product-thumb] img`);
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'eo-viewer__thumb';
      button.setAttribute('aria-label', `${photoLabel} ${index + 1}: ${item.alt}`);
      const image = document.createElement('img');
      image.src = source?.currentSrc || source?.src || item.msrc;
      image.alt = ''; image.width = 70; image.height = 70; image.loading = 'lazy';
      button.append(image);
      listen(button, 'click', () => viewer.goTo(index));
      strip.append(button);
    });
    const thumbnails = Array.from(strip.children);
    const syncZoom = () => {
      if (!viewer.currItem) return;
      const zoomed = viewer.getZoomLevel() > viewer.currItem.initialZoomLevel + .01;
      zoom.textContent = zoomed ? zoom.dataset.zoomOut : zoom.dataset.zoomIn;
      zoom.setAttribute('aria-label', zoom.textContent);
      zoom.setAttribute('aria-pressed', String(zoomed));
    };
    let syncFrame;
    const syncImageAlts = () => {
      dialog.querySelectorAll('.pswp__img').forEach(image => {
        const item = items.find(item => image.src === item.src || image.src === item.msrc);
        if (item) {
          image.alt = item.alt;
          image.closest('.pswp__item')?.setAttribute('aria-hidden', String(item !== items[viewer.getCurrentIndex()]));
        }
      });
      syncZoom();
    };
    const imageObserver = new MutationObserver(() => {
      cancelAnimationFrame(syncFrame);
      syncFrame = requestAnimationFrame(syncImageAlts);
    });
    imageObserver.observe(dialog.querySelector('.pswp__container'), { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'src'] });
    const update = () => {
      const index = viewer.getCurrentIndex();
      count.textContent = `${photoLabel} ${index + 1} / ${items.length}`;
      caption.textContent = items[index].alt;
      thumbnails.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
      keepInView(strip, thumbnails[index]);
      syncImageAlts();
    };
    // Reserve room for the toolbar and filmstrip inside PhotoSwipe's image sizing.
    viewer.listen('parseVerticalMargin', item => {
      const top = dialog.querySelector('[data-eo-viewer-top]').getBoundingClientRect().height + 12;
      const bottom = dialog.querySelector('.eo-viewer__bottom').getBoundingClientRect().height + 12;
      viewer.options.barsSize = { top, bottom };
      item.vGap.top = top;
      item.vGap.bottom = bottom;
    });
    viewer.options.scaleMode = 'fit';
    viewer.options.fitControlsWidth = 0;
    viewer.options.timeToIdle = 0;
    if (reducedMotion()) {
      viewer.options.showAnimationDuration = 0;
      viewer.options.hideAnimationDuration = 0;
    }
    viewer.options.closeElClasses = ['item'];
    viewer.listen('preventDragEvent', (e, isDown, prevent) => {
      if (e.target.closest('.pswp__ui')) prevent.prevent = false;
    });
    viewer.listen('afterChange', update);
    viewer.listen('initialZoomInEnd', () => { update(); requestAnimationFrame(() => close.focus({ preventScroll: true })); });
    viewer.listen('imageLoadComplete', syncImageAlts);
    listen(zoom, 'click', () => {
      const fit = viewer.currItem.initialZoomLevel;
      const zoomed = viewer.getZoomLevel() > fit + .01;
      const target = zoomed ? fit : Math.max(fit, Math.min(1, fit * 2.5));
      viewer.zoomTo(target, { x: viewer.viewportSize.x / 2, y: viewer.viewportSize.y / 2 }, reducedMotion() ? 0 : 220);
      // The transform observer updates the label for buttons, click, and pinch zoom.
      syncZoom();
    });
    listen(dialog.querySelector('[data-eo-film-prev]'), 'click', () => strip.scrollBy({ left: -strip.clientWidth * .75, behavior: reducedMotion() ? 'instant' : 'smooth' }));
    listen(dialog.querySelector('[data-eo-film-next]'), 'click', () => strip.scrollBy({ left: strip.clientWidth * .75, behavior: reducedMotion() ? 'instant' : 'smooth' }));
    listen(dialog, 'keydown', e => {
      if (e.key === 'Tab') {
        const controls = Array.from(dialog.querySelectorAll('button:not([hidden]):not([disabled]), a[href]')).filter(el => el.getClientRects().length);
        const first = controls[0], last = controls[controls.length - 1];
        if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
      if (e.key === 'Home' || e.key === 'End') { e.preventDefault(); viewer.goTo(e.key === 'Home' ? 0 : items.length - 1); }
    });
    // Keep page controls out of keyboard and screen-reader navigation while open.
    const background = [];
    let ancestor = dialog;
    while (ancestor && ancestor !== document.body) {
      Array.from(ancestor.parentElement.children).forEach(el => {
        if (el !== ancestor && !['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(el.tagName)) background.push(el);
      });
      ancestor = ancestor.parentElement;
    }
    const originalInert = background.map(el => el.inert);
    const originalAriaHidden = background.map(el => el.getAttribute('aria-hidden'));
    background.forEach(el => { el.inert = true; el.setAttribute('aria-hidden', 'true'); });
    const oldOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    viewer.listen('destroy', () => {
      controller.abort(); imageObserver.disconnect(); cancelAnimationFrame(syncFrame);
      background.forEach((el, i) => {
        el.inert = originalInert[i];
        originalAriaHidden[i] === null ? el.removeAttribute('aria-hidden') : el.setAttribute('aria-hidden', originalAriaHidden[i]);
      });
      document.documentElement.style.overflow = oldOverflow;
      dialog.classList.remove('eo-viewer'); dialog.removeAttribute('aria-modal');
      strip.replaceChildren();
      returnFocus?.focus({ preventScroll: true });
    });
  });

  const boot = scope => scope.querySelectorAll('[data-eo-gallery]').forEach(setupGallery);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('quickview:loaded', () => boot(document));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-eo-gallery]').forEach(root => galleries.get(root)?.()));
})();
